import { FC } from 'react';

import { FEATURE_FLAGS } from '@telialt/common-cms/src/CustomJson/constants';
import { useHybrisConfiguration } from '@telialt/common-cms/src/CustomJson/hooks/useHybrisConfiguration';
import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { ORDER_CART_ENTRY_ID } from '@telialt/common-order-cart/src/constants';
import { DeliveryEntry } from '@telialt/common-order-cart/src/entries/DeliveryEntry';
import { PromotionEntry } from '@telialt/common-order-cart/src/entries/PromotionEntry';
import { SimCardEntry } from '@telialt/common-order-cart/src/entries/SimCardEntry';
import { CartActionData, CartHeaderData, CartTotalsData, OrderCartProps } from '@telialt/common-order-cart/src/model';
import { OrderCart } from '@telialt/common-order-cart/src/OrderCart';
import { StockLevelStatuses } from '@telialt/models/src/EshopModel';
import { useLogErrorEshop } from '@telialt/utils/src/datadog/useLogError';
import {
    selectDeliveryCost,
    selectLeasingPlanPrice,
    selectOrderPromotions,
    selectPossibleSapRatePlans,
    selectProductInCart,
    selectProductStock,
    selectWithInsurance,
} from '@telialt/utils/src/eshopSelectors';
import {
    isEntryWithDevice,
    isEntryWithDisney,
    isEntryWithESimCard,
    isEntryWithGift,
    isEntryWithNetflix,
    isEntryWithRatePlan,
    isEntryWithSimCard,
    isEntryWithUpsell,
} from '@telialt/utils/src/eshopTypeGuards';
import { TeliaSegment, useSegment } from '@telialt/utils/src/segment';

import { useCartQuery } from '../hooks/queries/useCartQuery';
import { useIsProductPageMutating } from '../hooks/useIsProductPageMutating';
import { getLocalizedStockShipmentDate } from '../ProductStockStatus/utils';
import { DEFAULT_ENTRY_ORDER } from './constants';
import { DiscountEntryConnected } from './entries/DiscountEntryConnected';
import { DisneyVasConnected } from './entries/DisneyVasConnected';
import { GiftEntryConnected } from './entries/GiftEntryConnected';
import { InsuranceEntryConnected } from './entries/InsuranceEntryConnected';
import { MainProductEntryConnected } from './entries/MainProductEntryConnected';
import { MobRatePlanEntryConnected } from './entries/MobRatePlanEntryConnected';
import { NetflixVasConnected } from './entries/NetflixVasConnected';
import { UpsellEntryConnected } from './entries/UpsellEntryConnected';

type PartialActionData = Pick<CartActionData, 'onClick' | 'variant'> & {
    text?: string;
    url?: string;
    hasArrow?: boolean;
    isDisabled?: boolean;
    isLoading?: boolean;
};

export type HybrisOrderCartProps = {
    header?: Partial<CartHeaderData>;
    totals?: {
        isTotalPricesWithoutRatePlan?: boolean;
        totalPriceLabel?: string;
        monthlyPriceLabel?: string;
        cartTotalsRef?: CartTotalsData['cartTotalsRef'];
    };
    action?: PartialActionData;
    showDiscountForm?: boolean;
    showValuePrompts?: boolean;
    showEntryActions?: boolean;
    showRatePlanRelatedEntries?: boolean;
};

export const HybrisOrderCart: FC<HybrisOrderCartProps> = ({
    header,
    totals,
    action,
    showDiscountForm = false,
    showValuePrompts = false,
    showEntryActions = false,
    showRatePlanRelatedEntries = true,
}) => {
    const { t } = useTranslations();
    const { cart } = useCartQuery();
    const segment = useSegment();

    const product = selectProductInCart(cart);
    const stock = selectProductStock(cart);
    const hasPossibleSapRatePlans = selectPossibleSapRatePlans(cart)?.length > 0;
    const hasLeasingPlanPrice = !!selectLeasingPlanPrice(cart);

    const cartHeader: OrderCartProps['header'] = {
        expandable: header?.expandable || 'MOBILE_ONLY',
        isDefaultExpanded: header?.isDefaultExpanded ?? false,
        isForceExpanded: header?.isForceExpanded ?? false,
        title: header?.title || t['eshop.order_cart.order_summary'],
        titleTag: header?.titleTag,
    };

    const preorderBanner: OrderCartProps['preorderBanner'] = {
        isVisible: stock?.stockLevelStatus === StockLevelStatuses.FutureStock,
        shipmentDate: getLocalizedStockShipmentDate(stock),
    };

    const monthlyPrice = totals?.isTotalPricesWithoutRatePlan
        ? cart.totalMonthlyPriceWithoutRatePlan
        : cart.totalMonthlyPrice;

    const hasMissingTotalMonthlyPrice =
        hasPossibleSapRatePlans && totals?.isTotalPricesWithoutRatePlan && !cart.totalMonthlyPriceWithoutRatePlan;
    useLogErrorEshop(
        hasMissingTotalMonthlyPrice && new Error('totalMonthlyPriceWithoutRatePlan is missing in cart data'),
    );

    const hasMissingTotalPrice =
        hasPossibleSapRatePlans && totals?.isTotalPricesWithoutRatePlan && !cart.totalPriceWithoutRatePlan;
    useLogErrorEshop(hasMissingTotalPrice && new Error('totalPriceWithoutRatePlan is missing in cart data'));

    const selectedTotalPrice = totals?.isTotalPricesWithoutRatePlan ? cart.totalPriceWithoutRatePlan : cart.totalPrice;

    const totalPrice =
        selectedTotalPrice?.value > 0
            ? selectedTotalPrice
            : {
                  value: 0,
                  formattedValue: '0,00 €',
              };

    const cartTotals: OrderCartProps['cartTotals'] = {
        totalPrice,
        monthlyPrice: monthlyPrice?.value > 0 ? monthlyPrice : undefined,
        totalPriceLabel: totals?.totalPriceLabel || t['eshop.order_cart.total_price_label'],
        monthlyPriceLabel:
            totals?.monthlyPriceLabel ||
            (segment === TeliaSegment.B2c && !hasLeasingPlanPrice && t['eshop.order_cart.monthly_price_label']) ||
            undefined,
        cartTotalsRef: totals?.cartTotalsRef,
    };

    const { data: isPurchaseDisabled } = useHybrisConfiguration(FEATURE_FLAGS.PURCHASE_DISABLED);
    const isVisibleCartAction = stock?.stockLevelStatus !== StockLevelStatuses.NoStock && isPurchaseDisabled !== 'true';
    const isDisabledCartAction = useIsProductPageMutating();
    const cartAction: OrderCartProps['cartAction'] = {
        isVisible: isVisibleCartAction,
        type: undefined,
        url: action?.url,
        variant: action?.variant,
        hasArrow: action?.hasArrow ?? false,
        text: action?.text || t['eshop.order_cart.primary_action.text'],
        onClick: action?.onClick,
        isDisabled: action?.isDisabled ?? isDisabledCartAction,
        isLoading: action?.isLoading ?? isDisabledCartAction,
    };

    const cartSecondaryAction: OrderCartProps['cartSecondaryAction'] = { isVisible: false };

    const legalText = product?.legalText;
    const cartLegalText: OrderCartProps['cartLegalText'] = { isVisible: !!legalText, legalText };

    const cartOutOfStock: OrderCartProps['cartOutOfStock'] = {
        isVisible: stock?.stockLevelStatus === StockLevelStatuses.NoStock && isPurchaseDisabled !== 'true',
    };

    return (
        <OrderCart
            header={cartHeader}
            preorderBanner={preorderBanner}
            cartTotals={cartTotals}
            cartAction={cartAction}
            cartSecondaryAction={cartSecondaryAction}
            cartVatNote={{ isVisible: true }}
            cartValuePrompts={{ isVisible: showValuePrompts }}
            cartLegalText={cartLegalText}
            cartOutOfStock={cartOutOfStock}
            cartTotalsNotification={{ isVisible: false }}
        >
            {cart.entries
                .map((entry) => {
                    if (isEntryWithDevice(entry)) {
                        return (
                            <MainProductEntryConnected
                                key={`${ORDER_CART_ENTRY_ID.MAIN_PRODUCT}-${entry.entryNumber}`}
                                entry={entry}
                            />
                        );
                    }
                    if (isEntryWithRatePlan(entry) && showRatePlanRelatedEntries) {
                        return (
                            <MobRatePlanEntryConnected
                                key={`${ORDER_CART_ENTRY_ID.MOB_RATE_PLAN}-${entry.entryNumber}`}
                                entry={entry}
                            />
                        );
                    }
                    if (isEntryWithSimCard(entry) && showRatePlanRelatedEntries) {
                        return (
                            <SimCardEntry
                                key={`${ORDER_CART_ENTRY_ID.SIM_CARD}-${entry.entryNumber}`}
                                isEsim={false}
                                price={entry.totalPrice}
                            />
                        );
                    }
                    if (isEntryWithESimCard(entry) && showRatePlanRelatedEntries) {
                        return (
                            <SimCardEntry
                                key={`e-${ORDER_CART_ENTRY_ID.SIM_CARD}-${entry.entryNumber}`}
                                isEsim={true}
                                price={entry.totalPrice}
                            />
                        );
                    }
                    if (isEntryWithUpsell(entry)) {
                        return (
                            <UpsellEntryConnected
                                key={`${ORDER_CART_ENTRY_ID.UPSELL}-${entry.entryNumber}`}
                                entry={entry}
                                showRemove={showEntryActions}
                            />
                        );
                    }
                    if (isEntryWithGift(entry)) {
                        return (
                            <GiftEntryConnected
                                key={`${ORDER_CART_ENTRY_ID.GIFT}-${entry.entryNumber}`}
                                entry={entry}
                            />
                        );
                    }
                    // Netflix is always bundled with rate plan, so it should be hidden together
                    if (isEntryWithNetflix(entry) && showRatePlanRelatedEntries) {
                        return (
                            <NetflixVasConnected
                                key={`${ORDER_CART_ENTRY_ID.NETFLIX_VAS}-${entry.entryNumber}`}
                                entry={entry}
                                showSwitch={showEntryActions}
                            />
                        );
                    }
                    // Disney is always bundled with rate plan, so it should be hidden together
                    if (isEntryWithDisney(entry) && showRatePlanRelatedEntries) {
                        return (
                            <DisneyVasConnected
                                key={`${ORDER_CART_ENTRY_ID.DISNEY_VAS}-${entry.entryNumber}`}
                                entry={entry}
                                showSwitch={showEntryActions}
                            />
                        );
                    }

                    return null;
                })
                .concat(
                    <DeliveryEntry key="delivery" price={selectDeliveryCost(cart)} />,
                    showDiscountForm && <DiscountEntryConnected key="voucher" />,
                    selectWithInsurance(cart) && (
                        <InsuranceEntryConnected key="insurance" showRemove={showEntryActions} />
                    ),
                    ...(selectOrderPromotions(cart) || []).map((promotion, promotionIdx) => (
                        <PromotionEntry name={promotion.description} key={promotionIdx} />
                    )),
                )
                .filter(Boolean)
                .sort((a, b) => {
                    const aIndex = DEFAULT_ENTRY_ORDER.findIndex((Component) => Component === a.type);
                    const bIndex = DEFAULT_ENTRY_ORDER.findIndex((Component) => Component === b.type);

                    return aIndex - bIndex;
                })}
        </OrderCart>
    );
};
